import React, { FC, HTMLAttributes, useEffect, useState } from 'react'
import { Counter, ProductUnitType } from '@engyalo/storefront-components'
import { Button, Box, Heading, Image, Text } from '@chakra-ui/core'
import { Price } from '../Price'
import 'lazysizes'
import { useRecoilState, useRecoilValue } from 'recoil'
import { useTranslation } from 'react-i18next'
import { Hook, IStorefrontSettings } from '@engyalo/storefront-types'
import { ICartDictionary, IProduct, IOrder } from '@engyalo/storefront-types/lib/plugins/model'
import { getStoreFront_orderConfig as IOrderConfig } from '@engyalo/storefront-types/lib/graphql/storefront'
import { removeProductFromCartStart, addProduct, removeProduct, updateQuantity } from '@engyalo/storefront-events'
import { cartState, configState, orderConfigState, orderState } from '../../recoil/atoms'
import { generateCartId } from '../../lib/cartIdGenerator'
import { getTotalPrice } from '../../lib/getTotalPrice'
import Plugin, { usePlugin } from '../../Plugin'
import { getActionScreen } from '../../utils/analytics'
import { useLocation } from 'react-router'
import { skipIfInPreviewMode } from '../../utils/mode'

interface IProps extends HTMLAttributes<HTMLDivElement> {
  product: IProduct
  deleteItem: (product: IProduct) => void
}

const DEFAULT_UNIT_TYPE = {
  type: 'unit',
  interval: 1,
  format: '#0',
}

export const CartItem: FC<IProps> = ({ product, deleteItem }: IProps) => {
  const location = useLocation()
  const { t } = useTranslation()
  const [cart, setCart] = useRecoilState<ICartDictionary>(cartState)
  const orderConfig = useRecoilValue<IOrderConfig>(orderConfigState)
  const config = useRecoilValue(configState)
  const storefrontPlugin = usePlugin(Hook.STOREFRONT)
  const hidePrices = (storefrontPlugin?.props as IStorefrontSettings)?.hidePrices ?? false
  const [unitType, setUnitType] = useState<ProductUnitType>(DEFAULT_UNIT_TYPE)

  const session = {
    userID: orderConfig.userID || '',
    customer: config.customer,
  }

  useEffect(() => {
    const unitType = product?.metadata?.unitTypes?.find(
      (unitType: ProductUnitType) => unitType.type === product?.sizeDescription
    )
    if (unitType) {
      setUnitType(unitType)
    }
  }, [product])

  const getProductFirstImage = (product: IProduct): string => {
    if (product && product.imageURL) return product.imageURL[0] || ''
    return ''
  }

  const addToCart = (product: IProduct, quantity = 1, shouldReplace = false): void => {
    const tempCart = { ...cart }
    if (product && product.id) {
      const currentSku = generateCartId(product)
      if (tempCart[currentSku]) {
        const tempProd = { ...tempCart[currentSku] }
        if (tempProd.quantity) {
          const totalPrice = getTotalPrice(product)
          tempProd.quantity = shouldReplace ? quantity : tempProd.quantity + quantity
          if (tempProd.quantity > 0) {
            tempCart[currentSku] = {
              ...tempProd,
              totalPrice,
            }
            setCart(tempCart)
          } else {
            deleteItem(tempCart[currentSku])
          }
        }
      } else if (quantity > 0) {
        const totalPrice = getTotalPrice(product)
        tempCart[currentSku] = {
          ...product,
          totalPrice,
          quantity,
          cartId: currentSku,
        } as IProduct
      }
      setCart(tempCart)
    }
  }

  if (!product) {
    return null
  }

  const unitTypes = product.metadata?.unitTypes
  const hasUnitTypes = unitTypes && unitTypes.length > 0

  return (
    <Box borderBottom="1px solid" borderColor="gray.200" display="flex" padding="8px">
      <Box flex="2" display="flex">
        <Box display="flex">
          <Box w="4rem !important" display="flex">
            <Image
              width="4rem"
              height="4rem"
              display="flex"
              data-src={getProductFirstImage(product)}
              className="lazyload"
            />
          </Box>
          <Box paddingLeft="8px" display="flex" flexDirection="column">
            <Heading marginY="4px" as="h3" size="sm" flex="1">
              {product.name}
            </Heading>
            <Box fontSize="xs" fontFamily="body" color="gray.100" paddingBottom="4px" display="flex" flexDir="column">
              <Text>{product.description}</Text>
              {hasUnitTypes && <Text>{cart[product.cartId ?? ''].sizeDescription}</Text>}
            </Box>
            <Plugin hook={Hook.CART_ITEM_DETAIL} pluginProps={{ cart, product, tFunction: t }}>
              <></>
            </Plugin>
            {!hidePrices && (
              <Price
                price={product.totalPrice ? product.totalPrice : product.price}
                currency={config.currencyPrefix}
                currencyFormat={config.currencyFormat}
              />
            )}
            <Button
              width="4.2rem"
              color="red.500"
              variant="link"
              border="transparent"
              fontSize="sm"
              textDecor="underline"
              backgroundColor="transparent"
              fontWeight="normal"
              onClick={(): void => {
                skipIfInPreviewMode(() => {
                  removeProductFromCartStart({
                    product: {
                      id: product.id || '',
                      sku: product.sku || '',
                      name: product.name || '',
                      category: product.category || '',
                      price: product.price || 0,
                      totalPrice: product.price && product.quantity ? product.price * product.quantity : 0,
                    },
                    session: {
                      userID: orderConfig.userID || '',
                      customer: config.customer,
                    },
                  })
                })
                deleteItem(product)
              }}
            >
              {t('cartItem-delete')}
            </Button>
          </Box>
        </Box>
      </Box>
      <Box flex="1">
        <Counter
          name={product.cartId || ''}
          addAction={(): void => {
            skipIfInPreviewMode(() => {
              addProduct({
                product: {
                  id: product.id || '',
                  sku: product.sku || '',
                  name: product.name || '',
                  category: product.category || '',
                  price: product.price || 0,
                  note: '',
                  screen: getActionScreen(location),
                },
                session,
              })
            })
            addToCart(product, unitType.interval)
          }}
          minusAction={(): void => {
            skipIfInPreviewMode(() => {
              removeProduct({
                product: {
                  id: product.id || '',
                  sku: product.sku || '',
                  name: product.name || '',
                  category: product.category || '',
                  price: product.price || 0,
                  note: '',
                  screen: getActionScreen(location),
                },
                session,
              })
            })
            addToCart(product, -unitType.interval)
          }}
          onInputBlur={(event: React.FocusEvent<HTMLInputElement>): void => {
            const quantity = parseInt(event.target.value)
            skipIfInPreviewMode(() => {
              updateQuantity({
                product: {
                  id: product.id || '',
                  sku: product.sku || '',
                  name: product.name || '',
                  category: product.category || '',
                  price: product.price || 0,
                  note: '',
                  quantity,
                  screen: getActionScreen(location),
                },
                session,
              })
            })
            addToCart(product, quantity, true)
          }}
          value={cart[product.cartId || ''] ? cart[product.cartId || ''].quantity : 0}
          format={unitType.format}
        />
      </Box>
    </Box>
  )
}
